import { ApplicationRef, computed, Injectable, signal } from '@angular/core'
import { BehaviorSubject, Subscription, delay, forkJoin, map, of, retry,  shareReplay,  timer } from 'rxjs'
import { QuestionAndAnswer, Feedback, ResearchSession, ResearchHistory, AnswerResponse, StatusTypes, Document, InitResponse, AnswerContext, User, Answer, Advanced } from '../types'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import mockChatMessages from './mock.qandas'
import { Router } from '@angular/router'
import { ConfigService } from './config.service'
import { MessagehandlerService } from './messagehandler.service'
import { UserService } from './user.service'
import { TranslocoService } from '@jsverse/transloco'
import { toObservable } from '@angular/core/rxjs-interop'


@Injectable({
   providedIn: 'root'
})
export class ResearchService {

   // all the research sessions for this user (will be displayed in sidebar)
   public researchHistory!: ResearchHistory

   // the current qanda session from the researchHistory
   private currentResearchSession!: ResearchSession

   // currently used when the currentResearchSession is really changed to something new - the 'currentResearchSession' should be reactive, then this is not needed
   private currentResearchSession$ = new BehaviorSubject<ResearchSession | undefined>(undefined)

   // for the streaming server sent events functionality
   private evtSource!: EventSource

   private initialized: boolean

   public debugInfo: string[] = []
   // so we can unsubscribe and/or hook into the API call from other places
   private apiCall!: Subscription

   private runningAnswer?: string

   // maybe this should go to some service, leaving it here for now
   private errorStatusTypes = new Map<StatusTypes, boolean>()
   // a little easter egg
   private knownAnswers = ['aG93IG1hbnkgcm9hZHMgbXVzdCBhIG1hbiB3YWxrIGRvd24=', 'dWx0aW1hdGUgcXVlc3Rpb24gb2YgbGlmZQ==', 'bWVhbmluZyBvZiBsaWZl', 'dWx0aW1hdGUgYW5zd2Vy']
      .map((answer) => new RegExp(atob(answer), 'i'))

   get currentResearchSessionId() {
      return this.currentResearchSession?.id ?? 'session-not-initialized'
   }
   // convenience method
   public get clientKey() {
      return this.user.clientKey
   }

   // convenience method
   public get userId() {
      return this.user.userId
   }

   public get isDebug() {
      return this.config.debug ?? false
   }

   public get user(): User {
      return this.userService.user
   }

   public get researchSession() {
      return this.currentResearchSession
   }

   public get qandas() {
      return this.currentResearchSession?.qandas || []
   }

   public get onSessionChanged() {
      return this.currentResearchSession$.asObservable()
   }

   private isLoading$ = new BehaviorSubject(false)

   currentQandA = signal<QuestionAndAnswer | undefined>(undefined)
   currentAdvancedOptions = computed(() => { return this.currentQandA()?.answer?.advanced })

   currentQandA$ = toObservable(this.currentQandA)
   // used to fill the inputbox with a question when clicking 'more questions' or 'try again'
   currentQuestion$ = new BehaviorSubject<{question: string, regenerate: boolean}>({question: '', regenerate: false})

   public onLoadingChanged() {
      return this.isLoading$.asObservable()
   }

   constructor(private http: HttpClient,
      private router: Router,
      private config: ConfigService,
      private message: MessagehandlerService,
      private appRef: ApplicationRef,
      private translocoService: TranslocoService,
      private userService: UserService) {

      this.initialized = false

      translocoService.selectTranslate('research.status.answering').subscribe(() => {
         // this is just to make sure the translations are loaded
         if(!this.initialized) {
            this.initializeUser()
         }
         this.initializeStatusMessages()
      })
   }

   initializeUser() {
      this.startNewResearchSession()
      this.initialized = true

      // TODO here we should initialize research sessions from API /session endpoint instead of just adding one empty one
      // only add this for standalone, webcomponent has user knowledge server side
      let optionalQueryParams = ''
      if (this.config.currentConfig?.isStandalone) {
         optionalQueryParams = `user=${this.user.userId}&clientKey=${this.user.clientKey}`
      }
      if (this.config.debug) {
         if (optionalQueryParams != '') {
            optionalQueryParams += '&'
         }
         optionalQueryParams += 'debug=true'
      }

      this.http
         .post<InitResponse>(`${this.config.apiUrl}/init?${optionalQueryParams}`, {}).subscribe({
            next: (value) => {
               this.config.currentConfig.enableOnline = value.online ?? false
               if (value.features) {
                  value.features.forEach((feature) => {
                     // add a dummy feature change handler, needed for UI only debug features
                     // making it explicit to avoid some typescript warnings
                     feature.change = (value) => { console.log('Feature changed', feature.key, value)}
                  })
                  this.config.setFeatures(value.features)
               }
               this.user.maxRemaining = value.maxRemaining
               this.user.remaining = value.remaining
               // if we get researchsessions from the API, use them
               if (value.researchSessions?.length > 0) {
                  // add empty qandas / documents - these are not in the initial response, only loaded after the user selects a session in the sidebar
                  this.researchHistory.researchSessions =  [...this.researchHistory.researchSessions, ...value.researchSessions.map((session) => { return { ...session, qandas: [] } })]
               }
            }, error: (err) => {
               console.error(err)
            }
         })
   }

   mockChats(qandas: QuestionAndAnswer[] = mockChatMessages) {
      for (const qanda of qandas) {
         qanda.id = this.getId(qanda.question, 'mock')
         this.qandas.push(qanda)
      }
   }

   private async getAnswerStream(question: string, advancedOptions: Advanced | null | undefined): Promise<void>{
      this.isLoading$.next(true)
      // if the user removed the 'new research' session, but still asks a question - create a new session
      if (this.researchHistory.researchSessions.length === 0) {
         this.startNewResearchSession()
      }

      // TODO determine if the router usage here is correct! Should maybe be in different place (especially because of web component usage)
      // make sure we're at the right page
      if ((this.router?.url?.indexOf('research') === -1 || this.router?.url?.indexOf('research/new') > -1)) {
         await this.routeIfNeeded('/research')
      }

      const newQandA: QuestionAndAnswer = { id: this.getId(question, 'user'), question: question, answer: { answer$: new BehaviorSubject<string>(''), advanced: advancedOptions ?? undefined} }

      this.qandas.push(newQandA)
      this.cancelOngoingRequests()
      if (this.evtSource) {
         this.evtSource.close()
         this.appRef.tick()
      }
      this.registerStatus(newQandA, 'searching')

      let advanced = ''
      if (this.config.currentConfig?.showAdvanced) {
         // create the advanced query string with query params from keys

         if (advancedOptions) {
            for (const [key, value] of Object.entries(advancedOptions)) {
               if(typeof value !== 'undefined' && value !== null) {
                  advanced += `&${key}=${encodeURIComponent(value)}`
               }
            }
         }
      }
      let optionalQueryParams = ''
      if (this.config.product === 'summon') {
         optionalQueryParams += `sessionId=${this.currentResearchSession.id}`
      }

      this.evtSource = new EventSource(`${this.config.apiUrl}/answer-stream?query=${question}&user=${this.userId}&clientKey=${this.clientKey}&${optionalQueryParams}&ui_language=${this.translocoService.getActiveLang()}${advanced}`)
      this.evtSource.onerror = (e) => {
         newQandA.error = true
         console.error(e)
         this.evtSource.close()
         this.handleError(newQandA, e)
         this.appRef.tick()
      }

      this.currentResearchSession.qandas.push(newQandA)
      this.evtSource.onmessage = (e) => {
         const msg = JSON.parse(e.data)
         if (e === null) {
            console.log('EMPTY!')
            return
         }
         else if (msg.results) {
            if (newQandA.answer) {
               newQandA.answer.results = msg.results as Document[]
            }

            this.registerStatus(newQandA, 'answering')
         }
         else if (msg.error) {
            newQandA.error = true
         }
         else if (msg.status === 'summary') {
            this.runningAnswer = ''
            newQandA.answer = this.pick(msg.summary, 'id', 'createdAt', 'language', 'moreQuestions', 'advanced', 'improvedQuery', 'improvedQuerySearch', 'message', 'results', 'feedback', 'answer')

            if (newQandA.answer) {
               newQandA.answer.feedback = {id: 'tempAnswer' + this.currentResearchSession.qandas.findIndex(qanda => qanda.id === newQandA.id), type: 'answer' }
            }
            newQandA.answer?.results?.map((result:Document) => result.feedback = result.feedback ? result.feedback : { id: `${msg.id}-${result.id}`, query: question, type: 'record'})
            this.finalizeGetAnswer(newQandA, msg.remaining)

            this.evtSource.close()
            newQandA.answer?.answer$?.complete()
         }
         else if (msg.status === 'answer') {
            if (msg.answer === ''){
               msg.answer = '\n\n'
            }
            this.runningAnswer = this.runningAnswer ? this.runningAnswer.concat(msg.answer) : msg.answer
            if (newQandA.answer) {
               newQandA.answer.answer = this.runningAnswer?.slice()
            }
            newQandA.answer?.answer$?.next(msg.answer)
         }
         this.appRef.tick()
      }
   }

   setAnswer(question: string, regenerate = false) {
      this.currentQuestion$.next({question, regenerate})
   }

   async getAnswer(question: string, advanced: Advanced | null | undefined, regenerate = false, resultCount = 5): Promise<Subscription> {
      this.cancelOngoingRequests()
      let qandaToRegenerate = null
      if(regenerate)  {
         // find the qanda with the same question, so we can regenerate it - if there are multiple with the same question, we just take the last one
         qandaToRegenerate = this.qandas.findLast((qanda) => qanda.question === question)
      }
      // eslint-disable-next-line no-constant-condition
      if(this.config.currentConfig.streamAnswer) {
         this.getAnswerStream(question, advanced)
         // just return an empty subscription
         return Promise.resolve(new Subscription())
      }
      else {
         return this.getAnswerNonStreaming(question, advanced, qandaToRegenerate, resultCount)
      }
   }

   private async getAnswerNonStreaming(question: string, advancedOptions: Advanced | null | undefined, qandaToRegenerate: QuestionAndAnswer | null = null, resultCount = 5): Promise<Subscription> {
      this.message.emitAppEvent({ event:'qanda', status:'requesting', value: question })
      this.isLoading$.next(true)
      // if the user removed the 'new research' session, but still asks a question - create a new session
      if (this.researchHistory?.researchSessions?.length === 0) {
         this.startNewResearchSession()
      }

      await this.routeIfNeeded('/research')

      let newQandA: QuestionAndAnswer = { id: this.getId(question + new Date(), 'user'), question: question, answer: { advanced: advancedOptions ?? undefined} }

      if (qandaToRegenerate) {
         this.registerStatus(newQandA, 'regenerating')
      }

      this.registerStatus(newQandA, 'searching')
      // send the users question as a qanda, only when we're not regenerating the question
      this.qandas.push(newQandA)

      const cache = !qandaToRegenerate

      // fake delay ¯\_(ツ)_/¯
      const answering = of({}).pipe(delay(600), map(() => {
         this.registerStatus(newQandA, 'answering')
      }))

      let optionalQueryParams = ''
      // only add this for standalone, webcomponent has user knowledge server side
      if (this.config.currentConfig?.isStandalone) {
         optionalQueryParams = `&user=${this.userId}&clientKey=${this.clientKey}`
      }
      if (this.config.debug) {
         optionalQueryParams += '&debug=' + (this.createDebugString() ?? 'true')
         optionalQueryParams += `&cache=${this.config.debugCache}`
      }
      else {
         optionalQueryParams += `&cache=${cache}`
      }
      let advanced = ''
      if (this.config.currentConfig?.showAdvanced) {
         // create the advanced query string with query params from keys

         if (advancedOptions) {
            for (const [key, value] of Object.entries(advancedOptions)) {
               if(typeof value !== 'undefined' && value !== null) {
                  advanced += `&${key}=${encodeURIComponent(value)}`
               }
            }
         }
      }
      if (this.config.product === 'summon') {
         optionalQueryParams += `&sessionId=${this.currentResearchSession.id}`
      }
      const apiUrl = `${this.config.apiUrl}/answer?query=${encodeURIComponent(question)}${advanced}&resultcount=${resultCount}&ui_language=${this.translocoService.getActiveLang()}${optionalQueryParams}`
      const httpRequest = this.http
         .get<AnswerResponse>(apiUrl).pipe(
            retry({
               count: 5, delay: (err: HttpErrorResponse) => {
                  if (err.status === 504) {
                     console.log('retrying due to 504')
                     return timer(1000)
                  }
                  throw err
               }
            }),
            map((response, ) => { return response}),
            shareReplay())

      const apiRequest = forkJoin([answering, httpRequest])
         .pipe(map((results) => {
            return results[1]
         }))


      this.apiCall =
         apiRequest.subscribe({
            next: (response) => {
               this.message.emitAppEvent({ event:'qanda', status:'ready', value: question})
               // make sure we have a topic
               this.optUpdateResearchsessionTopic(question)

               if (response.answer) {
                  // this goes over the qandas in the current session and updates the one that was just answered
                  // since the question is already added to the qandas, but the answer is not yet there
                  this.currentResearchSession.qandas = this.currentResearchSession.qandas.map((qanda) => {
                     if (qanda.id === newQandA.id) {
                        newQandA = { ...newQandA, answer: this.pick(response, 'id', 'createdAt', 'advanced', 'answer', 'language', 'moreQuestions', 'improvedQuery', 'improvedQuerySearch', 'message', 'results', 'feedback', 'context') }
                        const url = 'https://sqa02-na04.alma.exlibrisgroup.com/discovery/search?search_scope=MyInst_and_CI&vid=EXLDEV1_INST:Alma&query=any,contains,' + encodeURIComponent(newQandA.answer?.improvedQuery ?? '')
                        if (newQandA.answer) {
                           if (!newQandA.answer?.improvedQuerySearch) {
                              newQandA.answer.improvedQuerySearch = url
                           }
                           else {
                              // create a full URL for the improved query search so possible encoding issues are tackled
                              const url = new URL(newQandA.answer.improvedQuerySearch, window.location.origin)
                              newQandA.answer.improvedQuerySearch = url.toString()
                           }

                        }
                        newQandA.answer?.context?.map((context:AnswerContext) => context.message = this.translateContext(context, { improvedQuerySearch: newQandA.answer?.improvedQuerySearch ?? '' }))
                        // now, if nothing comes back from API, add empty feedback objects, so we can use them later on
                        if (newQandA.answer) {
                           newQandA.answer.feedback = response.feedback ? response.feedback : { id: response.id, type: 'answer', query: question }
                           newQandA.answer.results?.map((result: Document) => result.feedback = result.feedback ? result.feedback : { id: `${response.id}-${result.id}`, query: question, type: 'record' })
                        }
                        return newQandA
                     }
                     else {
                        return qanda
                     }
                  })
                  // updated the count of the session
                  // TODO this is super bad - we need to automatically do this with observables or signals & effects or something
                  this.currentResearchSession.count = this.currentResearchSession.qandas.filter(qanda => qanda.answer?.answer).length
                  this.researchHistory.researchSessions.forEach((session) => {
                     if(session.id === this.currentResearchSession.id) {
                        session.count = this.currentResearchSession.count
                     }
                  })
                  // in web component, call this endpoint to save the qanda to the session
                  if (this.config.currentConfig.enableHistory && this.user.type !== 'anonymous') {
                     // the newQandA needs to be sent to the API so they can store it in the session (POST /saveQuestion) (fire and forget, don't do anything with errors)
                     // also add current session ID to the request so they can store it accordingly
                     const saveQuestion: {answer?: Answer, question: string, sessionId: string, topic: string} =
                       { sessionId: this.currentResearchSession.id, topic: this.currentResearchSession.topic, ...this.pick(newQandA, 'answer', 'question')}

                     this.http.post(`${this.config.apiUrl}/saveQuestion`, saveQuestion).subscribe({
                        error: (error) => {
                           console.error(error, newQandA.id)
                        }
                     })
                  }

                  if (this.config.debug) {
                     if (response.logs) {
                        this.debugInfo = [ response.originalRequest ?? '', ...response.logs]
                     }
                     else {
                        this.debugInfo = [ response.originalRequest ?? '', 'no logs found']
                     }
                  }
               }
               this.finalizeGetAnswer(newQandA, response.remaining ?? -1)

               return newQandA
            }, error: (error) => {
               if (this.config.debug) {
                  if (error.error.logs) {
                     this.debugInfo = [ apiUrl, error.error.error, ...error.error.logs]
                  }
                  else {
                     this.debugInfo = [ apiUrl, error.error.error, 'no logs found']
                  }
               }

               this.handleError(newQandA, error)
            }
         })
      return this.apiCall
   }

   private handleError(newQandA: QuestionAndAnswer, error: HttpErrorResponse | Event, question = newQandA.question) {
      this.finalizeGetAnswer(newQandA)

      if(error instanceof HttpErrorResponse)  {
         if (error.status === 429) {
            this.registerStatus(newQandA, 'quotalimit', error.message)
            this.message.emitAppEvent({ event:'qanda', status:'error', error: 'quotalimit', value: question})
         }
         else if (error.status === 422) {
            this.registerStatus(newQandA, 'unsupported', error.message)
            this.message.emitAppEvent({ event:'qanda', status:'error', error: 'unsupported', value: question})
         }
         else if (error.status === 404) {
            this.registerStatus(newQandA, 'noresults')
            this.message.emitAppEvent({ event:'qanda',  status:'error', error: 'noresults', value: question})
         }
         else if (error.status === 401) {
            this.registerStatus(newQandA, 'unauthorized', 'To use the assistant, please log in.')
            this.message.emitAppEvent({ event:'qanda',  status:'error', error: 'unauthorized', value: question})

         }
         else {
            this.registerStatus(newQandA, 'error', error.message)
            this.message.emitAppEvent({event:'qanda',  status:'error', error: (error.status ?? 'unknown http status') + ' - ' + error.message, value: question})
         }
      }
      else {
         this.registerStatus(newQandA, 'error', 'unknown error')
         this.message.emitAppEvent({event:'qanda',  status:'error', error: 'unknown error', value: question})
      }
   }

   createDebugString() {
      const debugValues = this.config.features.map((feature) => {
         if(feature.value !== feature.defaultValue) {
            return `${feature.key}:${feature.value}`
         }
         return null
      })
      const result = debugValues.filter(s => s).join(',')
      return result === '' ? null : result
   }

   finalizeGetAnswer(newQandA?: QuestionAndAnswer, remaining?: number) {
      this.isLoading$.next(false)
      this.currentQandA.set(newQandA)
      // be explicit in if, since 0 is false
      if (typeof remaining !== 'undefined' && remaining !== null) {
         this.userService.remaining = remaining
      }
      if (newQandA) {
         newQandA.regenerating = false
         this.optUpdateResearchsessionTopic(newQandA.question)
         this.visitNewQandA(newQandA)
      }
   }

   visitNewQandA(newQandA: QuestionAndAnswer) {
      const knownAnswer = this.knownAnswers.find(ka => ka.test(newQandA.question))
      // a little easter egg ;)
      if (knownAnswer) {
         this.message.toast({ message: 'Psst... don\'t mind my AI colleague here, the answer is actually just \'42\'!', title: 'The Ultimate Answer', options: {}, type: 'info' })
         // don't do it again, once was enough
         this.knownAnswers = []
      }
   }

   async routeIfNeeded(url: string, queryParams?: HttpParams) {
      // TODO get rid of this thing probably, since we now have a router in main app & component
      if (this.router.config.length > 0) {
         await this.router.navigate([url], {queryParams})
      }
   }

   private createSession() {
      const currentResearchSessionId = this.getId(`${this.user.clientKey}-${this.user.userId}-${new Date().toISOString()}`, 'session')
      return  {
         id: currentResearchSessionId,
         topic: '',
         qandas: [],
         count: 0,
         creationDate: new Date().toISOString()
      }
   }

   startNewResearchSession() {
      // stop getting the possibly running operation
      this.finalizeGetAnswer()
      // unique ID based on user and timestamp

      if (!this.researchHistory) {
         this.currentResearchSession = this.createSession()
         // also update the subject, we should make it a subject in the future
         this.currentResearchSession$.next(this.currentResearchSession)
         this.researchHistory = {
            id: '',
            researchSessions: [
               this.currentResearchSession
            ]
         }
      }
      // only if there are questions in the session, start a new one, otherwise just keep the current one
      if (this.currentResearchSession.qandas.length > 0) {
         this.currentResearchSession = this.createSession()
         this.currentResearchSession$.next(this.currentResearchSession)
         this.researchHistory.researchSessions.push(this.currentResearchSession)
         this.message.emitAppEvent({ event: 'session', status: 'ready', value: 'new' })
      }
   }

   async removeHistoryEntry(session: ResearchSession) {
      this.cancelOngoingRequests()
      this.message.emitAppEvent({ event: 'session', status: 'requesting', value: 'remove' })

      const removeEntry = async (session: ResearchSession) => {
         this.researchHistory.researchSessions = this.researchHistory.researchSessions.filter((ses) => session.id !== ses.id)
         this.startNewResearchSession()
         await this.routeIfNeeded('/research/new')
      }

      return this.http.delete(`${this.config.apiUrl}/session/${session.id}`).subscribe({
         next: async () => {
            this.message.emitAppEvent({ event: 'session', status: 'ready', value: 'remove' })
            removeEntry(session)
         }, error: (error: HttpErrorResponse) => {
            removeEntry(session)
            this.message.emitAppEvent({ event: 'session', status: 'error', value: 'remove' })
            // To be expected for now - not implemented
            console.error(error)
         }
      })
   }

   generateTopicName(session: ResearchSession) {
      const postObject = session.qandas.map((qanda) => {
         return qanda.question
      })
      this.http.post<{topic: string}>(`${this.config.apiUrl}/session-topic`, postObject).subscribe({
         next: (response: { topic: string }) => {
            this.message.emitAppEvent({ event: 'session', status: 'ready', value: 'topic' })
            session.topic = response.topic
         }, error: (error) => {
            // To be expected for now - not implemented
            console.error(error)
            this.message.emitAppEvent({ event: 'session', status: 'error', value: 'topic' })
         }
      })

   }


   async loadHistoryEntry(session: ResearchSession) {
      if (session?.id === this.currentResearchSession?.id) {
         return await this.routeIfNeeded('/research')
      }
      const sessionFromHistory = this.researchHistory.researchSessions.find((ses) => session.id === ses.id)
      this.message.emitAppEvent({ event: 'session', status: 'requesting', value: 'load' })
      this.cancelOngoingRequests()
      if (sessionFromHistory) {
         this.currentResearchSession = sessionFromHistory
         this.currentResearchSession$.next(this.currentResearchSession)
         if (this.currentResearchSession.qandas.length > 0) {
            this.currentQandA.set(this.currentResearchSession.qandas[this.currentResearchSession.qandas.length - 1])
         }
         // and now update it from the API
         this.http.get<ResearchSession>(`${this.config.apiUrl}/session/${session.id}`).subscribe({
            next: (response) => {
               // make sure we have a fully populated object by creating an empty session and then merging the response into it
               const newSessionObject = this.createSession()
               if (response.qandas) {
                  this.currentResearchSession = {...newSessionObject, ...response}
                  this.currentResearchSession$.next(this.currentResearchSession)
                  if(this.currentResearchSession.qandas.length > 0) {
                     this.currentQandA.set(this.currentResearchSession.qandas[this.currentResearchSession.qandas.length - 1])
                  }
               }
               this.message.emitAppEvent({ event: 'session', status: 'ready', value: 'load' })
            }, error: (error) => {
               // To be expected for now - not implemented
               console.error(error)
               this.message.emitAppEvent({ event: 'session', status: 'error', value: 'load' })
            }
         })
      }
      await this.routeIfNeeded('/research')
   }

   removeFeedback(feedback: Feedback) {
      // remove the feedback reaction to update the UI thumbs icon
      feedback.reaction = ''
      feedback.comment = ''
      feedback.reasons = []
      this.message.emitAppEvent({event: 'feedback', status: 'requesting', value: 'remove'})
      let optionalQueryParams = ''
      if (this.config.product === 'summon') {
         optionalQueryParams += `&sessionId=${this.currentResearchSession.id}`
      }

      return this.http.delete<AnswerResponse>(`${this.config.apiUrl}/feedback?id=${feedback.id}${optionalQueryParams}`).subscribe({
         next: () => {
            this.message.emitAppEvent({event: 'feedback', status: 'ready', value: 'remove'})
            this.message.toast({ message: this.translocoService.translate('feedback.removed.success'), title: this.translocoService.translate('feedback.removed.success.title'), options: {}, type: 'info' })
         }, error: (error) => {
            console.error(error)
            // TODO add error message from server?
            this.message.emitAppEvent({event: 'feedback', status: 'error', error: error.message ?? 'unknown', value: 'remove'})
            this.message.toast({ message: this.translocoService.translate('feedback.removed.error'), title: this.translocoService.translate('feedback.removed.error.title'), options: {}, type: 'error' })
         }
      })
   }

   sendFeedback(feedback: Feedback) {
      feedback.userId = this.userId
      feedback.clientKey = this.clientKey
      this.message.emitAppEvent({event: 'feedback', status: 'requesting'})
      let optionalQueryParams = ''
      if (this.config.product === 'summon') {
         optionalQueryParams += `?sessionId=${this.currentResearchSession.id}`
      }
      return this.http.post<AnswerResponse>(`${this.config.apiUrl}/feedback${optionalQueryParams}`, feedback).subscribe({
         next: (updatedQandA) => {
            this.currentResearchSession.qandas = this.currentResearchSession.qandas.map((qanda) => {
               if (qanda.id === updatedQandA.id) {
                  qanda = { ...qanda, answer: this.pick(updatedQandA, 'id', 'createdAt', 'answer', 'advanced', 'moreQuestions', 'improvedQuery', 'improvedQuerySearch', 'results', 'feedback') }
                  // now add empty feedback objects, so we can use them later on
                  if (qanda.answer) {
                     qanda.answer.feedback = updatedQandA.feedback ? updatedQandA.feedback : { id: updatedQandA.id, type: 'answer' }
                     qanda.answer.results?.map((result: Document) => {
                        result.feedback = result.feedback ? result.feedback : { id: `${updatedQandA.id}-${result.id}`, query: updatedQandA.query, type: 'record' }
                     })
                  }
                  return qanda
               }
               else {
                  return qanda
               }
            })
            this.message.emitAppEvent({event: 'feedback', status: 'ready'})
            this.message.toast({ message: this.translocoService.translate('feedback.submitted.success'), title: this.translocoService.translate('feedback.submitted.success.title'), options: {}, type: 'info' })
         }, error: () => {
            // TODO add error message from server?
            this.message.emitAppEvent({event: 'feedback', status: 'error', value: 'Error sending feedback'})
            this.message.toast({ message: this.translocoService.translate('feedback.submitted.error'), title: this.translocoService.translate('feedback.submitted.error.title'), options: {}, type: 'error' })
         }
      })
   }

   close() {
      if (this.evtSource) {
         this.evtSource.close()
      }
   }

   private getLastQuestion() {
      if (this.qandas.length) {
         const lastQuestion = this.qandas[this.qandas.length - 1]
         if (lastQuestion) {
            return lastQuestion.question
         }
      }
      return ''
   }

   cancelOngoingRequests() {
      this.apiCall?.unsubscribe()
      this.isLoading$.next(false)
      this.evtSource?.close()
      // get the last unanswered question in this session
      const questionInFlight = this.qandas.find((qanda) => qanda.question && !qanda.answer?.answer)
      if (questionInFlight) {
         // we're canceling things so remove this question
         // TODO we could indicate a cancelation in the UI, but I think this is good enough, it does not behave weirdly I think
         this.currentResearchSession.qandas = this.currentResearchSession.qandas.filter((qanda) => qanda.id !== questionInFlight.id)
      }
   }

   /**
    * the below labels are for the transloco extractor to find the keys
    * t(research.status.error, research.status.noresults, research.status.quotalimit, research.status.unsupported, research.status.searching, research.status.converting, research.status.answering, research.status.regenerating, research.status.unauthorized)
   */
   private registerStatus(qanda: QuestionAndAnswer,  type: StatusTypes, info = '') {
      const statusMessage = {
         type: type,
         text: `research.status.${type}`,
         error: this.errorStatusTypes.get(type) ?? false,
         info
      }
      if (!qanda.statusMessages) {
         qanda.statusMessages = []
      }
      qanda.statusMessages.push(statusMessage)
      qanda.error = statusMessage.error
   }

   // Copied this from SO... want a unique ID based on the text so we know which questions are the same
   // may need to go when we get proper IDs from API
   private cyrb53(str: string, seed = 0) {
      let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed
      for(let i = 0, ch; i < str.length; i++) {
         ch = str.charCodeAt(i)
         h1 = Math.imul(h1 ^ ch, 2654435761)
         h2 = Math.imul(h2 ^ ch, 1597334677)
      }
      h1  = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
      h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
      h2  = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
      h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)

      return 4294967296 * (2097151 & h2) + (h1 >>> 0)
   }

   private getId(text: string, from: string) {
      return `${from}-${this.cyrb53(text)}`
   }

   // thanks to https://stackoverflow.com/questions/17781472/how-to-get-a-subset-of-a-javascript-objects-properties
   private pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]) {
      return Object.fromEntries(
         keys
            .filter(key => key in obj)
            .map(key => [key, obj[key]])
      ) as Pick<T, K>
   }

   private optUpdateResearchsessionTopic(topic: string) {
      // TODO this could also be an LLM call that generates a nice topic
      if (this.currentResearchSession.topic === '') {
         this.currentResearchSession.topic = topic
      }
   }

   private initializeStatusMessages() {
      this.errorStatusTypes.clear()
      this.errorStatusTypes.set('error', true)
      this.errorStatusTypes.set('noresults', true)
      this.errorStatusTypes.set('quotalimit', true)
      this.errorStatusTypes.set('unsupported', true)
   }

   /**
    * Translate the context message
    * the labels below are used by the transloco extractor to find the keys
    * t(answer.context.message.facet_info, answer.context.message.error, answer.context.message.info, answer.context.message.debug)
   */
   private translateContext(context: AnswerContext, optionalValues: Record<string, string>) {
      const key = `answer.context.message.${context.type.toLocaleLowerCase()}`
      let translation

      if(context.values || optionalValues) {
         let replaceValueObject: Record<string, string> = {}
         if(context.values)  {
            for(let i = 0; i < context.values.length; i++) {
               replaceValueObject[`value${i}`] = context.values[i]
            }
         }
         if(optionalValues) {
            replaceValueObject = {...replaceValueObject, ...optionalValues}
         }
         translation = this.translocoService.translate(key, replaceValueObject)
      }
      else {
         translation = this.translocoService.translate(key)
      }
      if (translation && translation !== key) {
         // when it's an info message, also add the message
         if (context.type.toLocaleLowerCase() === 'info' || context.type.toLocaleLowerCase() === 'debug') {
            return `${translation} ${context.message}`
         }
         else {
            return translation
         }
      }
      else {
         return context.message
      }
   }

}


